import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { undo } from '../../../reducers/undoable';
import { telemetry } from '../../../telemetry';
import {
  addPrompt,
  updatePrompt as updatePromptAction,
  deletePrompt,
  setSelectedPrompt,
  addEmptyPrompt as addEmptyPromptAction,
  removeEmptyPrompt as removeEmptyPromptAction,
} from '../../../reducers/promptLibraryReducer';
import { Prompt } from '@server/prompts';
import { usePromptLibraryFacade } from '../../../apis/facades/usePromptLibraryFacade';

export const usePrompt = () => {
  const { create_prompt, update_prompt, delete_prompt, optimize_prompt } = usePromptLibraryFacade();
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();

  const handleError = useCallback(
    (error: any, errorMessage: string, optimisticAction?: any) => {
      addNotification({
        message: errorMessage,
        variant: SweepNotificationVariant.Error,
      });
      dispatch(undo(optimisticAction));
      telemetry.captureError(errorMessage, error);
    },
    [addNotification, dispatch],
  );

  const createPrompt = useCallback(
    async (groupId: string, promptData: Omit<Prompt, 'id'>) => {
      try {
        const newPrompt = await create_prompt(promptData, groupId);
        dispatch(addPrompt({ groupId, prompt: newPrompt }));
      } catch (error) {
        handleError(error, 'Failed to create prompt');
      }
    },
    [dispatch, handleError, create_prompt],
  );

  const updatePrompt = useCallback(
    async (groupId: string, promptId: string, updates: Partial<Prompt>) => {
      const optimisticAction = updatePromptAction({ groupId, promptId, update: updates });
      try {
        dispatch(optimisticAction);
        await update_prompt(promptId, updates);
      } catch (error) {
        handleError(error, 'Failed to update prompt', optimisticAction);
      }
    },
    [dispatch, handleError, update_prompt],
  );

  const removePrompt = useCallback(
    async (groupId: string, promptId: string) => {
      const optimisticAction = deletePrompt({ groupId, promptId });
      try {
        dispatch(optimisticAction);
        await delete_prompt(promptId);
      } catch (error) {
        handleError(error, 'Failed to delete prompt', optimisticAction);
      }
    },
    [dispatch, handleError, delete_prompt],
  );

  const clonePrompt = useCallback(
    async (groupId: string, duplicatedPrompt: Prompt) => {
      try {
        const clonedPrompt = await create_prompt(
          {
            ...duplicatedPrompt,
            name: `Copy of ${duplicatedPrompt.name} `,
          },
          groupId,
        );
        dispatch(addPrompt({ groupId, prompt: clonedPrompt }));
      } catch (error) {
        handleError(error, 'Failed to duplicate prompt');
      }
    },
    [dispatch, handleError, create_prompt],
  );

  const optimizePrompt = useCallback(
    async (promptText: string) => {
      try {
        const optimizedPrompt = await optimize_prompt(promptText);
        return optimizedPrompt;
      } catch (error) {
        handleError(error, 'Failed to optimize prompt');
      }
    },
    [handleError, optimize_prompt],
  );

  const selectPrompt = useCallback(
    (prompt: Prompt) => {
      dispatch(setSelectedPrompt({ prompt }));
    },
    [dispatch],
  );

  const unselectPrompt = useCallback(() => {
    dispatch(setSelectedPrompt({ prompt: null }));
  }, [dispatch]);

  const addEmptyPrompt = useCallback(
    (groupId: string) => {
      dispatch(addEmptyPromptAction({ groupId }));
    },
    [dispatch],
  );

  const removeEmptyPrompt = useCallback(
    (groupId: string) => {
      dispatch(removeEmptyPromptAction({ groupId }));
    },
    [dispatch],
  );

  return {
    addEmptyPrompt,
    createPrompt,
    updatePrompt,
    removePrompt,
    clonePrompt,
    optimizePrompt,
    removeEmptyPrompt,

    selectPrompt,
    unselectPrompt,
  };
};
